import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Map from '../components/Map';
import Timeline from '../components/Timeline';
import Sidebar from '../components/Sidebar';
import TelemetryPanel from '../components/TelemetryPanel';
import ExportPanel from '../components/ExportPanel';
import FeatureSegmentationPanel from '../components/FeatureSegmentationPanel';
import TrimPanel from '../components/TrimPanel';
import TelemetryIcon from '../components/icons/TelemetryIcon';
import ExportIcon from '../components/icons/ExportIcon';
import SegmentIcon from '../components/icons/SegmentIcon';
import Button from '../components/Button';
import { fetchDatasetById, fetchDatasetLocations, fetchFeatureSegments } from '../services/datasetsService';
import { useError } from '../contexts/ErrorContext';
import './Dataset1Page.css';

const Dataset1Page = () => {
  const navigate = useNavigate();
  const { datasetId } = useParams();
  const { addError } = useError();
  const [dataset, setDataset] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(null);
  const [activeTab, setActiveTab] = useState('telemetry'); // Open telemetry tab by default
  const playbackIntervalRef = useRef(null);
  const playbackSpeed = 100; // milliseconds per location point
  const [currentStats, setCurrentStats] = useState({ maxVelocitymps: 0, totVerticalMeters: 0, timestamp: null });
  const [statsHistory, setStatsHistory] = useState([]);
  const [sidebarWidth, setSidebarWidth] = useState(350);
  const [visualizedSegment, setVisualizedSegment] = useState(null); // only one segment can be visualized
  const [segments, setSegments] = useState([]);

  // Find current location based on currentTime
  const currentLocation = useMemo(() => {
    if (!locations || locations.length === 0 || currentTime === null) return null;
    
    // Find the location closest to currentTime
    return locations.reduce((prev, curr) => {
      return Math.abs(curr.timestamp - currentTime) < Math.abs(prev.timestamp - currentTime) 
        ? curr 
        : prev;
    });
  }, [locations, currentTime]);

  // Initialize stats history and current stats when locations are loaded
  useEffect(() => {
    if (locations && locations.length > 0) {
      // Extract all unique stats entries (sorted by timestamp)
      const allStats = [];
      const seenStats = new Set();
      
      locations.forEach(loc => {
        if (loc.stats && !seenStats.has(loc.stats.timestamp)) {
          seenStats.add(loc.stats.timestamp);
          allStats.push({
            timestamp: loc.stats.timestamp,
            maxVelocitymps: loc.stats.maxVelocitymps,
            totVerticalMeters: loc.stats.totVerticalMeters
          });
        }
      });
      
      // Sort by timestamp
      allStats.sort((a, b) => a.timestamp - b.timestamp);
      
      // Initialize with 0 values at the start time
      const startTime = locations[0].timestamp;
      setStatsHistory([
        { timestamp: startTime, maxVelocitymps: 0, totVerticalMeters: 0 },
        ...allStats
      ]);
      
      // Initialize current stats at 0
      setCurrentStats({ maxVelocitymps: 0, totVerticalMeters: 0, timestamp: startTime });
    }
  }, [locations]);

  // Update stats when currentLocation changes (preserve previous stats if new location doesn't have stats)
  useEffect(() => {
    if (currentLocation) {
      if (currentLocation.stats) {
        setCurrentStats({
          maxVelocitymps: currentLocation.stats.maxVelocitymps,
          totVerticalMeters: currentLocation.stats.totVerticalMeters,
          timestamp: currentLocation.stats.timestamp
        });
      }
      // If no stats, keep previous stats (they're already preserved in state)
    }
  }, [currentLocation]);

  // Define sidebar tabs
  const sidebarTabs = [
    {
      id: 'telemetry',
      label: 'Telemetry',
      icon: <TelemetryIcon size={24} />
    },
    {
      id: 'export',
      label: 'Export',
      icon: <ExportIcon size={24} />
    },
    {
      id: 'feature-segmentation',
      label: 'Feature segmentation',
      icon: <SegmentIcon size={24} />
    },
    {
      id: 'trim',
      label: 'Trim',
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/><line x1="8.12" y1="8.12" x2="12" y2="12"/></svg>
    }
  ];

  useEffect(() => {
    const loadDataset = async () => {
      try {
        setLoading(true);
        const [datasetData, locationsData, segmentsData] = await Promise.all([
          fetchDatasetById(datasetId, addError),
          fetchDatasetLocations(datasetId, addError),
          fetchFeatureSegments(datasetId, addError).catch(() => [])
        ]);
        setDataset(datasetData);
        setLocations(locationsData);
        setSegments(Array.isArray(segmentsData) ? segmentsData : []);
        
        // Set initial time to first location
        if (locationsData && locationsData.length > 0) {
          setCurrentTime(locationsData[0].timestamp);
        }
      } catch (error) {
        const errorMsg = error.message || 'Failed to load dataset';
        addError(errorMsg, error.stack, 'Dataset Page');
        console.error('Failed to load dataset:', error);
      } finally {
        setLoading(false);
      }
    };

    if (datasetId) {
      loadDataset();
    }
  }, [datasetId, addError]);

  // Handle play/pause
  useEffect(() => {
    if (isPlaying && locations.length > 0 && currentTime !== null) {
      const maxTime = locations[locations.length - 1].timestamp;
      
      playbackIntervalRef.current = setInterval(() => {
        setCurrentTime(prevTime => {
          if (prevTime >= maxTime) {
            setIsPlaying(false);
            return maxTime;
          }
          
          // Find next location
          const nextLocation = locations.find(loc => loc.timestamp > prevTime);
          return nextLocation ? nextLocation.timestamp : maxTime;
        });
      }, playbackSpeed);
    } else {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
        playbackIntervalRef.current = null;
      }
    }

    return () => {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }
    };
  }, [isPlaying, locations, playbackSpeed, currentTime]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleTimeChange = (newTime) => {
    setCurrentTime(newTime);
    setIsPlaying(false); // Pause when manually changing time
  };

  // Calculate center from locations if available
  const centerLatitude = locations.length > 0 
    ? locations[Math.floor(locations.length / 2)].lat 
    : 39.6061;
  const centerLongitude = locations.length > 0 
    ? locations[Math.floor(locations.length / 2)].lon 
    : -106.3550;

  return (
    <div className="dataset1-page">
      <header className="dataset1-header">
        <div className="dataset1-header-content">
          <Button
            variant="outline"
            size="small"
            onClick={() => navigate('/')}
          >
            ← Back to Data Explorer
          </Button>
          <h1>{loading ? 'Loading...' : (dataset?.name || datasetId)}</h1>
        </div>
      </header>
      <main className="dataset1-main">
        <div className="dataset1-map-container">
          <Map 
            height="100%" 
            locations={locations}
            currentTime={currentTime}
            centerLatitude={centerLatitude}
            centerLongitude={centerLongitude}
            highlightedSegment={visualizedSegment}
          />
          {locations.length > 0 && currentTime !== null && (
            <Timeline
              locations={locations}
              currentTime={currentTime}
              onTimeChange={handleTimeChange}
              isPlaying={isPlaying}
              onPlayPause={handlePlayPause}
              sidebarOpen={activeTab !== null}
              sidebarWidth={sidebarWidth}
            />
          )}
        </div>
        <Sidebar
          tabs={sidebarTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          width={sidebarWidth}
          onWidthChange={setSidebarWidth}
          topOffset={50}
        >
          {activeTab === 'telemetry' && (
            <TelemetryPanel 
              currentLocation={currentLocation} 
              currentStats={currentStats} 
              statsHistory={statsHistory}
              currentTime={currentTime}
            />
          )}
          {activeTab === 'export' && (
            <ExportPanel
              locations={locations}
              currentTime={currentTime}
              datasetName={dataset?.name || datasetId}
              datasetId={datasetId}
              segments={segments}
            />
          )}
          {activeTab === 'trim' && (
            <TrimPanel
              datasetId={datasetId}
              locations={locations}
              currentTime={currentTime}
              dataset={dataset}
              onTrimChange={(trim) => setDataset(prev => ({ ...prev, ...trim }))}
            />
          )}
          {activeTab === 'feature-segmentation' && (
            <FeatureSegmentationPanel
              datasetId={datasetId}
              datasetName={dataset?.name || datasetId}
              locations={locations}
              currentTime={currentTime}
              referenceEpochSeconds={locations?.[0]?.timestamp ?? null}
              segments={segments}
              onSegmentsChange={setSegments}
              visualizedSegmentId={visualizedSegment?.id ?? null}
              onToggleVisualize={(segmentOrNull) => {
                setVisualizedSegment((prev) => {
                  if (!segmentOrNull) return null;
                  return prev && prev.id === segmentOrNull.id ? null : segmentOrNull;
                });
              }}
            />
          )}
        </Sidebar>
      </main>
    </div>
  );
};

export default Dataset1Page;

